// useReportsLandingRoute — where the left-nav REPORTS entry should land.
//
// Balance Sheet stays the default landing report. Once this user has favorited
// at least one saved report, the Reports Library becomes their hub and REPORTS
// lands on /reports/library instead — favorites signal they've curated a set
// of reports they navigate from.
//
// Live limit(1) listener rather than a one-shot read: favorites are toggled in
// the library itself, and a one-shot value would keep routing the user to the
// balance sheet for the rest of the session after they star their first
// report. Equality-only filters, so no composite index is needed; a missing
// `favorite` field simply doesn't match, which is correct (unstarred).

import { useContext, useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
} from "firebase/firestore";

import * as Sentry from "@sentry/react";

import * as ROUTES from "../constants/routes";
import UserContext from "../assets/user_context";
import { useAuth } from "../services/use-auth";

export const REPORTS_LIBRARY_ROUTE = "/reports/library";

export default function useReportsLandingRoute() {
  const { org, user } = useContext(UserContext);
  const { db } = useAuth();
  const uid = user?.uid;
  // Primitive state so unchanged snapshots bail out of re-rendering the
  // always-mounted nav (same idiom as setHasMultiSubscription in TopNav).
  const [hasFavorite, setHasFavorite] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!db || !org || !uid) {
      setHasFavorite(false);
      setReady(false);
      return undefined;
    }
    const favoritesQuery = query(
      collection(db, "orgs", org, "savedReports"),
      where("ownerUid", "==", uid),
      where("favorite", "==", true),
      limit(1),
    );
    return onSnapshot(
      favoritesQuery,
      (snap) => {
        setHasFavorite(!snap.empty);
        setReady(true);
      },
      // On listener error, fail to the default landing rather than blocking —
      // but surface it: a silent failure quietly disables favorites-landing.
      (error) => {
        Sentry.withScope((scope) => {
          scope.setTags({
            component: "useReportsLandingRoute",
            action: "subscribe-favorites",
            feature: "reports-library",
          });
          scope.setExtras({ orgId: org });
          Sentry.captureException(error);
        });
        setHasFavorite(false);
        setReady(true);
      },
    );
  }, [db, org, uid]);

  return {
    route: hasFavorite ? REPORTS_LIBRARY_ROUTE : ROUTES.REPORTS,
    ready,
  };
}
